import React, { FC } from "react";
import GallerySlider from "components/GallerySlider/GallerySlider";
import Badge from "shared/Badge/Badge";
import { currencySymbol } from "shared/CurrencySymbol";
import { Link } from "react-router-dom";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";

export interface SimilarCardProps {
  className?: string;
  similarProperties: PropertyToEdit;
}

const SimilarCard: FC<SimilarCardProps> = ({
  className = "",
  similarProperties,
}) => {
  const href = `/listing-stay-detail/${similarProperties?.slug}`;
  const address = `${similarProperties.city}${similarProperties.city && similarProperties.country ? ', ' : ''}${similarProperties.country}`;
  
  return (
    <div
      className={`nc-SimilarCard group bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden hover:shadow-xl hover:border-neutral-300 dark:hover:border-neutral-700 transition-all duration-200 ${className}`}
      data-nc-id="SimilarCard"
    >
      {/* Gallery Section */}
      <div className="relative w-full">
        <GallerySlider
          uniqueID={`SimilarCard_${similarProperties?.id}`}
          ratioClass="aspect-w-4 aspect-h-3"
          galleryImgs={
            similarProperties?.place_images?.map((image: any) =>
              String(image)
            ) || []
          }
          href={href}
        />
        
        {/* Floating Badge */}
        {similarProperties?.property_type?.name && (
          <div className="absolute top-3 left-3 z-10 pointer-events-none">
            <Badge
              name={similarProperties.property_type.name}
              className="text-xs font-semibold shadow-lg backdrop-blur-sm bg-white/90 dark:bg-neutral-900/90"
            />
          </div>
        )}
      </div>

      {/* Content Section - Wrapped in Link */}
      <Link to={href} className="block p-4">
        {/* Title */}
        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 line-clamp-2 mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-500 transition-colors">
          {similarProperties?.title || "Property"}
        </h3>

        {/* Location */}
        {address && (
          <div className="flex items-start gap-1.5 mb-3">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-neutral-500 dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-1">
              {address}
            </p>
          </div>
        )}

        {/* Price and Property Details */}
        <div className="flex items-end justify-between">
          {/* Price */}
          <div>
            <span className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              {currencySymbol()}{similarProperties?.price_per_month 
                ? Number(similarProperties.price_per_month).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                : "N/A"}
            </span>
            <span className="text-sm text-neutral-500 dark:text-neutral-400 ml-1">
              /mo
            </span>
          </div>

          {/* Beds & Baths */}
          <div className="flex items-center gap-3 text-sm text-neutral-600 dark:text-neutral-400">
            {similarProperties?.total_bedrooms && similarProperties.total_bedrooms > 0 && (
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
                <span>{similarProperties.total_bedrooms}</span>
              </div>
            )}
            {similarProperties?.total_bathrooms && similarProperties.total_bathrooms > 0 && (
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8h8V6z" clipRule="evenodd" />
                </svg>
                <span>{similarProperties.total_bathrooms}</span>
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default SimilarCard;